import {
  Box,
  Button,
  Flex,
  Paper,
  Text,
  Title,
  Input,
  Select,
  Grid,
  ActionIcon,
  Textarea,
  Center,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import useProfileStore from "../../store/useProfileStore";
import { IconPlus } from "../../shared/svg/AddIcon";
import { ExperienceEdit } from "../../types/profile-editing";
import {
  addExperience,
  deleteExperience,
  updateExperience,
} from "../../api/profileEditingApi";
import { ClearIcon } from "../../shared/svg/ClearIcon";
import { notifications } from "@mantine/notifications";
import { EditIconSmall } from "../../shared/svg/EditIconSmall";
import { TrashCanSmall } from "../../shared/svg/TrashCanSmall";
import { CheckIcon } from "../../shared/svg/CheckIcon";
import { useMediaQuery } from "@mantine/hooks";
import ConfirmDelete from "../../utils/components/ConfirmDelete";

const months = Array.from({ length: 12 }, (_, i) => {
  const value = (i + 1).toString();
  return { value, label: value.padStart(2, "0") };
});

const years = Array.from({ length: 100 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { value: year.toString(), label: year.toString() };
});

const getDaysInMonth = (month: number, year: number) => {
  const date = new Date(year, month, 0);
  const days = date.getDate();
  return Array.from({ length: days }, (_, i) => {
    return { value: (i + 1).toString(), label: (i + 1).toString() };
  });
};

interface Props {
  setChanges: (t: boolean) => void;
}

const ExperienceEditingWidget: React.FC<Props> = ({ setChanges }) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [experiences, setExperiences] = useState<ExperienceEdit[]>([]);
  const [originalExperiences, setOriginalExperiences] = useState<
    ExperienceEdit[]
  >([]);
  const { profile, fetchProfile, setUserMarketChanges } = useProfileStore();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const [errors, setErrors] = useState<
    Record<number, Partial<Record<string, string>>>
  >({});
  const matches = useMediaQuery("(max-width: 650px)");

  useEffect(() => {
    if (profile?.experiences.length) {
      setExperiences(profile.experiences);
      setOriginalExperiences(profile.experiences);
    }
  }, [profile]);

  useEffect(() => {
    fetchProfile();
  }, []);

  const validateExperience = (exp: ExperienceEdit) => {
    const newErrors: Record<string, string> = {};

    if (!exp.job_title?.trim()) newErrors.job_title = t("field_required");
    if (!exp.company?.trim()) newErrors.company = t("field_required");
    return newErrors;
  };

  const validateDate = (
    startDate: {
      day: number | null;
      month: number | null;
      year: number | null;
    } | null,
    endDate: {
      day: number | null;
      month: number | null;
      year: number | null;
    } | null
  ): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!startDate || !endDate) return errors;

    if (startDate.year && endDate.year && startDate.year > endDate.year) {
      errors.date_range = t("start_date_after_end");
    } else if (
      startDate.year === endDate.year &&
      startDate.month &&
      endDate.month &&
      startDate.month > endDate.month
    ) {
      errors.date_range = t("start_date_after_end");
    } else if (
      startDate.year === endDate.year &&
      startDate.month === endDate.month &&
      startDate.day &&
      endDate.day &&
      startDate.day > endDate.day
    ) {
      errors.date_range = t("start_date_after_end");
    }

    // Перевірка коректності дат
    if (
      startDate.day &&
      startDate.month &&
      startDate.year &&
      (startDate.day < 1 ||
        startDate.day > getDaysInMonth(startDate.month, startDate.year).length)
    ) {
      errors.start_date = t("invalid_start_date");
    }

    if (
      endDate.day &&
      endDate.month &&
      endDate.year &&
      (endDate.day < 1 ||
        endDate.day > getDaysInMonth(endDate.month, endDate.year).length)
    ) {
      errors.end_date = t("invalid_end_date");
    }

    return errors;
  };

  const clearFieldError = (index: number, fieldKey: string) => {
    setErrors((prev) => {
      const entry = prev[index];
      if (!entry || !entry[fieldKey]) return prev;

      const { [fieldKey]: _, ...rest } = entry;
      return {
        ...prev,
        [index]: rest,
      };
    });
  };

  const handleChange = (
    index: number,
    field:
      | keyof ExperienceEdit
      | keyof ExperienceEdit["start_date"]
      | keyof ExperienceEdit["end_date"],
    value:
      | string
      | { day: number | null; month: number | null; year: number | null }
      | boolean,
    isStartDate: boolean = false
  ) => {
    setExperiences((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              ...(field === "month" || field === "year" || field === "day"
                ? {
                    [isStartDate ? "start_date" : "end_date"]: {
                      ...item[isStartDate ? "start_date" : "end_date"],
                      [field]: value,
                    },
                  }
                : {
                    [field]: value,
                  }),
            }
          : item
      )
    );
  };

  const handleAddExperience = () => {
    // Перевіряємо, чи вже є незбережений досвід
    const hasUnsaved = experiences.some((exp) => exp.id === 0 && exp.isNew);

    if (hasUnsaved) return;

    const newExperience: ExperienceEdit & { isNew?: boolean } = {
      id: 0,
      job_title: "",
      company: "",
      location: "",
      responsibilities: "",
      start_date: { day: null, month: null, year: null },
      end_date: { day: null, month: null, year: null },
      is_current: false,
      isNew: true, // маркуємо як нову
    };

    setExperiences((prev) => [...prev, newExperience]);
    setOriginalExperiences((prev) => [...prev, newExperience]);

    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleRemoveExperience = async (index: number, id?: number) => {
    if (id) {
      try {
        await deleteExperience(id);
        setChanges(true);
        setUserMarketChanges(true);
      } catch (error) {
        console.error("Failed to delete experience", error);
      }
    }
    setExperiences((prev) => prev.filter((_, i) => i !== index));
    setOriginalExperiences((prev) => prev.filter((_, i) => i !== index));

    notifications.show({
      title: t("success"),
      message: t("delete_successfully"),
      icon: <TrashCanSmall />,
      color: "#d3d3d3",
      style: {
        border: "1px solid #d3d3d3",
        borderRadius: "8px",
      },
    });

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  const handleSaveExperience = async (index: number) => {
    const exp = experiences[index];

    const validationErrors = validateExperience(exp);

    const dateErrors = validateDate(exp.start_date, exp.end_date);

    if (
      Object.keys(validationErrors).length > 0 ||
      Object.keys(dateErrors).length > 0
    ) {
      setErrors((prev) => ({
        ...prev,
        [index]: { ...validationErrors, ...dateErrors },
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, [index]: {} }));
    try {
      let updated: ExperienceEdit;

      const expToSend: ExperienceEdit = {
        ...exp,
        start_date:
          exp.start_date === null
            ? { day: null, month: null, year: null }
            : {
                day:
                  exp.start_date.day?.toString() === "0"
                    ? null
                    : exp.start_date.day,
                month:
                  exp.start_date.month?.toString() === "0"
                    ? null
                    : exp.start_date.month,
                year:
                  exp.start_date.year?.toString() === "0"
                    ? null
                    : exp.start_date.year,
              },
        end_date:
          exp.end_date === null
            ? { day: null, month: null, year: null }
            : {
                day:
                  exp.end_date.day?.toString() === "0"
                    ? null
                    : exp.end_date.day,
                month:
                  exp.end_date.month?.toString() === "0"
                    ? null
                    : exp.end_date.month,
                year:
                  exp.end_date.year?.toString() === "0"
                    ? null
                    : exp.end_date.year,
              },
      };
      delete expToSend.isNew;

      if (exp.id === 0) {
        delete expToSend.id;

        updated = await addExperience(expToSend);
        notifications.show({
          title: t("success"),
          message: t("add_successfully"),
          icon: <CheckIcon />,
          color: "green",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
        setChanges(true);
        setUserMarketChanges(true);
      } else {
        updated = await updateExperience(expToSend);
        setChanges(true);
        setUserMarketChanges(true)
        notifications.show({
          title: t("success"),
          message: t("changed_successfully"),
          icon: <EditIconSmall />,
          color: "#CEB7FF",
          style: {
            border: "1px solid #d3d3d3",
            borderRadius: "8px",
          },
        });
      }

      setExperiences((prev) =>
        prev.map((item, i) => (i === index ? updated : item))
      );
      setOriginalExperiences((prev) =>
        prev.map((item, i) => (i === index ? updated : item))
      );
    } catch (error: any) {
      setErrors((prev) => ({
        ...prev,
        [index]: error.errors,
      }));
    }

    // await fetchProfile();
  };

  const handleResetExperience = (
    index: number,
    originalExp?: ExperienceEdit
  ) => {
    if (!originalExp) return;

    setExperiences((prev) =>
      prev.map((exp, i) =>
        i === index
          ? {
              ...originalExp,
              isNew: exp.isNew,
            }
          : exp
      )
    );
  };

  const isExperienceChanged = (
    current: ExperienceEdit & { isNew?: boolean },
    original?: ExperienceEdit
  ) => {
    if (!original) return true;

    const keys: (keyof ExperienceEdit)[] = [
      "job_title",
      "company",
      "location",
      "responsibilities",
      "start_date",
      "end_date",
      "is_current",
    ];

    return keys.some((key) => {
      if (key === "start_date" || key === "end_date") {
        return (
          current[key]?.month !== original[key]?.month ||
          current[key]?.year !== original[key]?.year ||
          current[key]?.day !== original[key]?.day
        );
      }
      return current[key] !== original[key];
    });
  };
  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        mb="lg"
        direction={matches ? "column" : "row"}
        gap={matches ? 20 : 10}
      >
        <Title size={32}>{t("experience")}</Title>
        <Button
          leftSection={<IconPlus />}
          onClick={handleAddExperience}
          variant="light"
          disabled={experiences.some((exp) => exp.id === 0 && exp.isNew)}
        >
          {t("add_experience")}
        </Button>
      </Flex>

      {experiences.length === 0 ? (
        <Center py="xl">
          <Paper
            radius="md"
            p="xl"
            withBorder
            shadow="sm"
            style={{ maxWidth: 1000, width: "100%" }}
          >
            <Flex
              align="center"
              w="100%"
              px={20}
              gap={matches ? 20 : 40}
              direction={matches ? "column" : "row"}
            >
              <Title
                order={2}
                fw={700}
                w={matches ? "100%" : "33%"}
                ta="left"
                style={{ fontSize: "1.5rem" }}
              >
                {t("nothing_here")}
              </Title>
              <Text
                size="xl"
                c="dimmed"
                w={matches ? "100%" : "66%"}
                style={{ fontSize: "1.125rem" }}
              >
                {t("no_experience")}
              </Text>
            </Flex>
          </Paper>
        </Center>
      ) : (
        <Flex direction="column" gap={30}>
          {experiences.map((exp, index) => {
            let originalExp = originalExperiences.find((e) => e.id === exp.id);
            return (
              <Paper
                withBorder
                p={matches ? 12 : 24}
                radius="md"
                key={exp.id}
                style={{ backgroundColor: "#f5f3fa", position: "relative" }}
              >
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  style={{ position: "absolute", top: 10, right: 10 }}
                  onClick={() => {
                    if (exp.id) {
                      setIsDeleteOpen(true);
                      setDeleteId(exp.id);
                      setDeleteIndex(index);
                    } else if (!exp.id) {
                      handleRemoveExperience(index);
                    }
                  }}
                >
                  <ClearIcon />
                </ActionIcon>

                <Grid gutter="sm">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Input.Wrapper
                      withAsterisk
                      label={t("job_title")}
                      error={errors[index]?.job_title}
                    >
                      <Input
                        value={exp.job_title}
                        onChange={(e) => {
                          handleChange(
                            index,
                            "job_title",
                            e.currentTarget.value
                          );
                          clearFieldError(index, "job_title");
                        }}
                      />
                    </Input.Wrapper>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Input.Wrapper
                      withAsterisk
                      label={t("company")}
                      error={errors[index]?.company}
                    >
                      <Input
                        value={exp.company}
                        onChange={(e) => {
                          clearFieldError(index, "company");
                          handleChange(index, "company", e.currentTarget.value);
                        }}
                      />
                    </Input.Wrapper>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Input.Wrapper
                      error={errors[index]?.location}
                      label={t("location")}
                    >
                      <Input
                        value={exp.location}
                        onChange={(e) =>
                          handleChange(index, "location", e.currentTarget.value)
                        }
                      />
                    </Input.Wrapper>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <Input.Wrapper label={t("responsibilities")}>
                      <Textarea
                        value={exp.responsibilities}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "responsibilities",
                            e.currentTarget.value
                          )
                        }
                        autosize
                        minRows={1}
                        style={{ fontSize: "16px" }}
                      />
                    </Input.Wrapper>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text fw={500} mb={4}>
                      {t("start_date")}
                    </Text>
                    <Flex>
                      <Grid.Col span={4}>
                        <Input.Wrapper
                          label={t("year")}
                          error={errors[index]?.start_year}
                        >
                          <Select
                            placeholder={t("year")}
                            data={years}
                            value={exp.start_date?.year?.toString() || ""}
                            onChange={(val) => {
                              handleChange(index, "year", val || "0", true);
                              clearFieldError(index, "start_year");
                            }}
                            size={matches ? "xs" : "sm"}
                          />
                        </Input.Wrapper>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Input.Wrapper
                          label={t("month")}
                          error={errors[index]?.start_month}
                        >
                          <Select
                            placeholder={t("month")}
                            data={months}
                            value={
                              exp.start_date?.month != null
                                ? exp.start_date.month.toString()
                                : null
                            }
                            onChange={(val) => {
                              handleChange(index, "month", val || "0", true);
                              clearFieldError(index, "start_month");
                            }}
                            disabled={!exp.start_date?.year} // Місяць вимикається, якщо немає року
                            size={matches ? "xs" : "sm"}
                          />
                        </Input.Wrapper>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Input.Wrapper
                          label={t("day")}
                          error={errors[index]?.start_day}
                        >
                          <Select
                            placeholder={t("day")}
                            data={getDaysInMonth(
                              exp.start_date?.month || 0,
                              exp.start_date?.year || 0
                            )}
                            value={
                              exp.start_date?.day != null
                                ? exp.start_date.day.toString()
                                : null
                            }
                            onChange={(val) => {
                              handleChange(index, "day", val || "0", true);
                              clearFieldError(index, "start_day");
                            }}
                            disabled={
                              !exp.start_date?.month || !exp.start_date?.year
                            } // День вимикається, якщо немає місяця або року
                            size={matches ? "xs" : "sm"}
                          />
                        </Input.Wrapper>
                      </Grid.Col>
                    </Flex>
                  </Grid.Col>

                  {/* End Date */}
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text fw={500} mb={4}>
                      {t("end_date")}
                    </Text>
                    <Flex>
                      <Grid.Col span={4}>
                        <Input.Wrapper
                          label={t("year")}
                          error={errors[index]?.end_year}
                        >
                          <Select
                            placeholder={t("year")}
                            data={years}
                            value={
                              exp.is_current
                                ? null
                                : exp.end_date?.year != null
                                ? exp.end_date.year.toString()
                                : null
                            }
                            onChange={(val) => {
                              handleChange(index, "year", val || "0", false);
                              clearFieldError(index, "end_year");
                            }}
                            disabled={exp.is_current}
                            size={matches ? "xs" : "sm"}
                          />
                        </Input.Wrapper>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Input.Wrapper
                          label={t("month")}
                          error={errors[index]?.end_month}
                        >
                          <Select
                            placeholder={t("month")}
                            data={months}
                            value={
                              exp.is_current
                                ? null
                                : exp.end_date?.month != null
                                ? exp.end_date.month.toString()
                                : null
                            }
                            onChange={(val) => {
                              handleChange(index, "month", val || "0", false);
                              clearFieldError(index, "end_month");
                            }}
                            disabled={!exp.end_date?.year || exp.is_current} // Місяць вимикається, якщо немає року
                            size={matches ? "xs" : "sm"}
                          />
                        </Input.Wrapper>
                      </Grid.Col>
                      <Grid.Col span={4}>
                        <Input.Wrapper
                          label={t("day")}
                          error={errors[index]?.end_day}
                        >
                          <Select
                            placeholder={t("day")}
                            data={getDaysInMonth(
                              exp.end_date?.month || 0,
                              exp.end_date?.year || 0
                            )}
                            value={
                              exp.end_date?.day != null
                                ? exp.end_date.day.toString()
                                : null
                            }
                            onChange={(val) => {
                              handleChange(index, "day", val || "0", false);
                              clearFieldError(index, "end_day");
                            }}
                            disabled={
                              !exp.end_date?.month ||
                              !exp.end_date?.year ||
                              exp.is_current
                            } // День вимикається, якщо немає місяця або року
                            size={matches ? "xs" : "sm"}
                          />
                        </Input.Wrapper>
                      </Grid.Col>
                    </Flex>
                    <Checkbox
                      mt="md"
                      ml={6}
                      label={t("is_current")}
                      checked={exp.is_current}
                      onChange={(e) => {
                        const isChecked = e.currentTarget.checked;
                        handleChange(index, "is_current", isChecked);

                        // Якщо is_current чекнутий, скидаємо end_date
                        if (isChecked) {
                          handleChange(index, "end_date", {
                            day: null,
                            month: null,
                            year: null,
                          });
                        }
                      }}
                      styles={{
                        body: {
                          alignItems: "center",
                        },
                      }}
                    />
                  </Grid.Col>
                </Grid>

                <Flex
                  align="center"
                  justify={matches ? "center" : "end"}
                  direction={matches ? "column" : "row"}
                  mt="lg"
                  gap={20}
                >
                  {errors[index]?.date_range && (
                    <Text color="#B00020" size="xs" mt={4}>
                      {errors[index].date_range}
                    </Text>
                  )}
                  {errors[index]?.non_field_errors && (
                    <Text color="#B00020" size="xs" mt={4}>
                      {errors[index].non_field_errors}
                    </Text>
                  )}
                  <Flex align="center" gap={20}>
                    <Button
                      variant="light"
                      style={{ flexShrink: 0 }}
                      color="gray"
                      onClick={() => {
                        if (!exp.id) {
                          handleRemoveExperience(index);
                        } else {
                          handleResetExperience(index, originalExp);
                        }
                      }}
                      disabled={
                        !exp.id ? false : !isExperienceChanged(exp, originalExp)
                      }
                    >
                      {t("cancel")}
                    </Button>

                    <Button
                      variant="light"
                      style={{ flexShrink: 0 }}
                      color="green"
                      onClick={() => handleSaveExperience(index)}
                      disabled={
                        !exp.id ? false : !isExperienceChanged(exp, originalExp)
                      }
                    >
                      {t("save")}
                    </Button>
                  </Flex>
                </Flex>
              </Paper>
            );
          })}
          <div ref={scrollRef} />
        </Flex>
      )}
      <ConfirmDelete
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteId(null);
          setDeleteIndex(null);
        }}
        onDelete={() => {
          if (deleteId !== null && deleteIndex !== null)
            handleRemoveExperience(deleteIndex, deleteId);
        }}
        title={t("delete_experience_title")}
      />
    </Box>
  );
};

export default ExperienceEditingWidget;
